import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import { ScrollDispatcher } from "@angular/cdk/scrolling";
import { Overlay } from "@angular/cdk/overlay";
import { Subject, takeUntil } from "rxjs";

import {
    LgLocalizationModule,
    LgTranslateService,
    useTranslationNamespace,
} from "@logex/framework/lg-localization";
import { IDropdownDefinition, LgOverlayService, UiCoreModule } from "@logex/framework/ui-core";

import { OverlayBase } from "@codman/shared/ui-overlay";
import {
    IBenchmarkDimension,
    IComparisonType,
    IConnectProvider,
    IMultiDataSelection,
    Provider,
} from "@codman/shared/feature-benchmarking";

import {
    ComparisonPopupEditClickEvent,
    ComparisonPopupEditInitProps,
    SharedComparisonPopupEditComponent,
} from "../comparison-picker-popup-edit/comparison-picker-popup-edit.component";
import { IComparisonPickerSource } from "../../comparison-picker/comparison-picker.component";

@Component({
    selector: "codman-comparison-picker-button-edit",
    templateUrl: "./comparison-picker-button-edit.component.html",
    styleUrls: ["./comparison-picker-button-edit.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, LgLocalizationModule, UiCoreModule],
    providers: [useTranslationNamespace("APP._Shared.ComparisonPicker")],
})
export class SharedComparisonPickerButtonEditComponent extends OverlayBase<
    ComparisonPopupEditInitProps,
    ComparisonPopupEditClickEvent
> {
    private _lgTranslateService = inject(LgTranslateService);

    @Input() allSources: IComparisonPickerSource[] = [];
    @Input() selectedProviders: Provider[] = [];
    @Input() selectedProvidersMultiData: IMultiDataSelection | null | undefined;
    @Input() comparisonType: IComparisonType | null = "basic";
    @Input() selectedConnectProviders: number[] | null | undefined;
    @Input() availableConnectProviders: IConnectProvider[] | null = [];
    @Input() benchmarkDimension: IBenchmarkDimension | null | undefined;

    @Output() readonly clickEvent = new EventEmitter<ComparisonPopupEditClickEvent>();

    _providersDefinition: IDropdownDefinition<string> = {
        groups: [],
    };
    _currentProvider: Provider = "ownProvider";

    private readonly _destroy$ = new Subject<void>();

    constructor(
        _elementRef: ElementRef,
        _scrollDispatcher: ScrollDispatcher,
        _overlayService: LgOverlayService,
        _overlay: Overlay,
    ) {
        super(
            _elementRef,
            _scrollDispatcher,
            _overlayService,
            _overlay,
            SharedComparisonPopupEditComponent,
        );

        this._providersDefinition.groups = this.allSources.map(source => ({
            id: source.type,
            name: this._lgTranslateService.translate(
                source.type === "connectBenchmark" ? ".Connect.Title" : (source.text ?? ""),
            ),
        }));
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        super._onDestroy();
    }

    _openDialog(): void {
        this._showPopup(
            {
                allSources: this.allSources,
                selectedProviders: this.selectedProviders,
                selectedProvidersMultiData: this.selectedProvidersMultiData,
                comparisonType: this.comparisonType,
                selectedConnectProviders: this.selectedConnectProviders ?? [],
                availableConnectProviders: this.availableConnectProviders ?? [],
                benchmarkDimension: this.benchmarkDimension,
            },
            {
                positions: ["bottom-left", "bottom-center"],
            },
        )
            .pipe(takeUntil(this._destroy$))
            .subscribe(
                event => this.clickEvent.emit(event),
                err => console.error(err),
                () => this._hidePopup(),
            );
    }
}
